import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _formatErrorMessage from "@mui/x-internals/formatErrorMessage";
import _extends from "@babel/runtime/helpers/esm/extends";
const _excluded = ["children"];
import { EMPTY_ARRAY } from '@base-ui/utils/empty';
import { generateId } from '@base-ui/utils/generateId';
import { warnOnce } from '@mui/x-internals/warning';
import { processEvent } from "../../../process-event/index.mjs";
import { dateToEventString } from "../date-utils.mjs";

/**
 * Determines if the occurrence placeholder has changed in a meaningful way that requires updating the store.
 */
export function shouldUpdateOccurrencePlaceholder(adapter, previous, next) {
  if (next == null || previous == null) {
    return next !== previous;
  }
  const untypedPrevious = previous;
  const untypedNext = next;

  // Compare keys present in `next`.
  for (const key in untypedNext) {
    if (key === 'start' || key === 'end') {
      if (!adapter.isEqual(untypedNext[key], untypedPrevious[key])) {
        return true;
      }
    } else if (!Object.is(untypedNext[key], untypedPrevious[key])) {
      return true;
    }
  }

  // Catch keys present in `previous` but removed from `next` (e.g. `isHidden`).
  for (const key in untypedPrevious) {
    if (!(key in untypedNext)) {
      return true;
    }
  }
  return false;
}
export const DEFAULT_EVENT_MODEL_STRUCTURE = {};
const EVENT_PROPERTIES_LOOKUP = {
  id: true,
  title: true,
  description: true,
  start: true,
  end: true,
  resource: true,
  rrule: true,
  allDay: true,
  readOnly: true,
  extractedFromId: true,
  exDates: true,
  color: true,
  draggable: true,
  resizable: true,
  className: true,
  timezone: true
};
const EVENT_PROPERTIES = Object.keys(EVENT_PROPERTIES_LOOKUP);
const RESOURCE_PROPERTIES_LOOKUP = {
  id: true,
  title: true,
  eventColor: true,
  children: true,
  areEventsDraggable: true,
  areEventsResizable: true,
  areEventsReadOnly: true
};
const RESOURCE_PROPERTIES = Object.keys(RESOURCE_PROPERTIES_LOOKUP);

/**
 * Converts an event model to a processed event using the provided model structure.
 */
export function getProcessedEventFromModel(model, adapter, eventModelStructure, displayTimezone, recurringEventsPlugin = null) {
  // 1. Convert the model to a default event model
  const modelInDefaultFormat = {};
  for (const key of EVENT_PROPERTIES) {
    // @ts-ignore
    const getter = eventModelStructure?.[key]?.getter;

    // @ts-ignore
    modelInDefaultFormat[key] = getter ? getter(model) : model[key];
  }

  // 2. Convert the default event model to a processed event
  return processEvent(modelInDefaultFormat, displayTimezone, adapter, recurringEventsPlugin);
}

/**
 * Updates an event model based on the provided changes and model structure.
 * Converts internal date objects (`TemporalSupportedObject`) to strings
 * before applying them to the user's model, because `SchedulerEvent` date
 * fields are strings. Respects the original string format (instant vs wall-time).
 */
export function getUpdatedEventModelFromChanges(oldModel, changes, eventModelStructure, adapter, originalBuiltInModel) {
  const dataTimezone = originalBuiltInModel.timezone ?? 'default';
  const stringified = _extends({}, changes);
  if (changes.start != null) {
    stringified.start = dateToEventString(adapter, changes.start, originalBuiltInModel.start, dataTimezone);
  }
  if (changes.end != null) {
    stringified.end = dateToEventString(adapter, changes.end, originalBuiltInModel.end, dataTimezone);
  }
  if (changes.exDates != null) {
    stringified.exDates = changes.exDates.map((d, i) => {
      const originalExDate = originalBuiltInModel.exDates?.[i];
      if (originalExDate) {
        return dateToEventString(adapter, d, originalExDate, dataTimezone);
      }
      // New exDate — match the format of start
      return dateToEventString(adapter, d, originalBuiltInModel.start, dataTimezone);
    });
  }
  if (changes.rrule != null && typeof changes.rrule === 'object' && changes.rrule.until != null) {
    const originalRRule = originalBuiltInModel.rrule;
    const originalUntilString = typeof originalRRule === 'object' ? originalRRule.until : undefined;
    const referenceString = originalUntilString ?? originalBuiltInModel.start;
    stringified.rrule = _extends({}, changes.rrule, {
      until: dateToEventString(adapter, changes.rrule.until, referenceString, dataTimezone)
    });
  }
  return createOrUpdateEventModelFromBuiltInEventModel(oldModel, stringified, eventModelStructure);
}

/**
 * Create an event model from a processed event using the provided model structure.
 */
export function createEventModel(event, eventModelStructure, adapter) {
  const id = generateId('event');
  const formatNewDate = value => {
    if (typeof value === 'string') {
      return value;
    }
    return adapter.toJsDate(value).toISOString();
  };

  // Internal callers (e.g. FormContent) may pass rrule.until as a TemporalSupportedObject.
  // Convert it to a string so the built-in model stays in SchedulerEvent format.
  const rrule = typeof event.rrule === 'object' && event.rrule.until != null && typeof event.rrule.until !== 'string' ? _extends({}, event.rrule, {
    until: formatNewDate(event.rrule.until)
  }) : event.rrule;
  const builtInEvent = _extends({}, event, {
    id,
    start: formatNewDate(event.start),
    end: formatNewDate(event.end),
    exDates: event.exDates?.map(formatNewDate),
    rrule
  });
  const model = createOrUpdateEventModelFromBuiltInEventModel(null, builtInEvent, eventModelStructure);
  return {
    id,
    model
  };
}
function createOrUpdateEventModelFromBuiltInEventModel(oldModel, changes, eventModelStructure) {
  let eventModel = oldModel == null ? {} : _extends({}, oldModel);
  const propertiesWithSetter = [];
  for (const key in changes) {
    if (changes.hasOwnProperty(key)) {
      const typedKey = key;
      const setter = eventModelStructure?.[typedKey]?.setter;
      if (setter) {
        // @ts-ignore
        propertiesWithSetter.push([setter, changes[key]]);
      } else if (changes[key] === undefined) {
        // @ts-ignore
        delete eventModel[key];
      }
      // If the property was set to its default value, remove it from the model
      else if (oldModel != null && key === 'allDay' && changes[key] === false) {
        // @ts-ignore
        delete eventModel[key];
      } else {
        // @ts-ignore
        eventModel[key] = changes[key];
      }
    }
  }
  for (const [setter, value] of propertiesWithSetter) {
    eventModel = setter(eventModel, value);
  }
  return eventModel;
}

/**
 * Converts a resource model to a processed resource using the provided model structure.
 */
export function getProcessedResourceFromModel(resource, resourceModelStructure) {
  const processedResource = {};
  for (const key of RESOURCE_PROPERTIES) {
    const getter = resourceModelStructure?.[key]?.getter;

    // @ts-ignore
    const resourceProperty = getter ? getter(resource) : resource[key];
    if (key === 'children' && Array.isArray(resourceProperty)) {
      // Process children recursively
      const children = resourceProperty.map(child => getProcessedResourceFromModel(child, resourceModelStructure));
      // @ts-ignore
      processedResource[key] = children;
      continue;
    }

    // @ts-ignore
    processedResource[key] = resourceProperty;
  }
  return processedResource;
}
/**
 * Throws if the resolved event id is missing.
 */
export function checkSchedulerEventIdIsValid(id, event) {
  if (id == null) {
    throw new Error(process.env.NODE_ENV !== "production" ? `MUI X Scheduler: All events must have a unique \`id\`.
Without an \`id\`, an event cannot be tracked and silently overwrites another event in the calendar state.
Add an \`id\` to every event, or set \`eventModelStructure.id.getter\` to derive one from your event model.
An event was provided without an \`id\`:
${JSON.stringify(event)}` : _formatErrorMessage(291, JSON.stringify(event)));
  }
}
export function buildEventsState(parameters, adapter, displayTimezone, recurringEventsPlugin = null) {
  const {
    events = EMPTY_ARRAY,
    eventModelStructure
  } = parameters;
  const eventIdList = [];
  const eventModelLookup = new Map();
  const processedEventLookup = new Map();
  for (const event of events) {
    const processedEvent = getProcessedEventFromModel(event, adapter, eventModelStructure, displayTimezone, recurringEventsPlugin);
    const {
      id
    } = processedEvent;
    checkSchedulerEventIdIsValid(id, event);
    if (eventModelLookup.has(id)) {
      if (process.env.NODE_ENV !== 'production') {
        warnOnce([`MUI X Scheduler: Two or more events share the same id "${String(id)}".`, 'Event ids must be unique. Only the last event with a given id is kept, the others are ignored.']);
      }
    } else {
      eventIdList.push(id);
    }
    eventModelLookup.set(id, event);
    processedEventLookup.set(id, processedEvent);
  }
  return {
    eventIdList,
    eventModelLookup,
    eventModelStructure,
    processedEventLookup,
    eventModelList: events
  };
}
export function buildResourcesState(parameters) {
  const {
    resources = EMPTY_ARRAY,
    resourceModelStructure
  } = parameters;
  const resourceIdList = [];
  const processedResourceLookup = new Map();
  const resourceChildrenIdLookup = new Map();
  const addResourceToState = processedResource => {
    const {
        children
      } = processedResource,
      resourceWithoutChildren = _objectWithoutPropertiesLoose(processedResource, _excluded);
    processedResourceLookup.set(processedResource.id, resourceWithoutChildren);
    if (children) {
      for (const child of children) {
        if (!resourceChildrenIdLookup.get(processedResource.id)) {
          resourceChildrenIdLookup.set(processedResource.id, []);
        }
        resourceChildrenIdLookup.get(processedResource.id)?.push(child.id);
        addResourceToState(child);
      }
    }
  };
  for (const resource of resources) {
    const processedResource = getProcessedResourceFromModel(resource, resourceModelStructure);
    resourceIdList.push(processedResource.id);
    addResourceToState(processedResource);
  }
  return {
    resourceIdList,
    processedResourceLookup,
    resourceModelStructure,
    resourceChildrenIdLookup
  };
}